import React, { useState, useEffect, useMemo, useRef } from "react";
import { Formik } from "formik";
import { Loadable } from '../../../common';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { Button, Grid, TextField } from "@mui/material";

import makeStyles from '@mui/styles/makeStyles';

import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../../partials/content/Portlet";
import { DocumentView } from '../ContractViewPage/DocumentView';
import { MODULE_TYPE_VENDOR } from "../../../store/constants/contract";
import { Card } from "react-bootstrap";
import { useData } from "@pure-ptr/react";
import { ContractInfo } from "../../../store/api/ai-api";

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
    },
    leftIcon: {
        marginRight: theme.spacing(1),
    },
    rightIcon: {
        marginLeft: theme.spacing(1),
    },
    iconSmall: {
        fontSize: 20,
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '85%',
    },
    textArea: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    margin: {
        margin: theme.spacing(1),
    },
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
    },
    formControl: {
        margin: theme.spacing(1),
    },
    title: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(1),
    }
}));

export default function DocumentUpload({
    intl, contract, contractsUploadPending, contractId, handleUploadCallback, module, vendorUploadPending,shouldDeleteDocument=false,
    actions: { contractUpload, vendorUpload } }) {
    const [botUploadPayload, setBotUploadPayload] = useState<any>({});
    const ContractsApi = new ContractInfo()
    


    if( contract ) {
        contractId = contract.id;
    }

    // Compute the actual contract ID to use
    const actualContractId = useMemo(() => {
        return contract?.id || contractId;
    }, [contract?.id, contractId]);

    // const { isPending, data: aiDocsInfo } = useData( async () => {
    //     if(!contract) return
    //     return await contract.info?.fetchDocuments();
    // }, [contract]);


        const {
            isPending: isUplooadingToBot,
            error:botUploadError,
            data: docUpload
        } = useData(async () => {
            if (Object.keys(botUploadPayload)?.length <= 0) return;
    
            let restructuredPayload = botUploadPayload?.docData?.find(
                item => item?.id === botUploadPayload?.id
            );
            restructuredPayload = {
                s3_key: restructuredPayload?.s3Key,
                filename: restructuredPayload?.documentName,
                contract_id: restructuredPayload?.contractId,
                document_id: botUploadPayload?.id,
                metadata: JSON.parse(restructuredPayload?.metadata),
                description: restructuredPayload?.description
            };
    
            setBotUploadPayload({});
    
            return await ContractsApi?.uploadDocumenToBot(
                restructuredPayload?.contract_id,
                restructuredPayload
            );

            
        }, [botUploadPayload]);
    




    const [pollTrigger, setPollTrigger] = useState(0);

    // Phase 1: Initial status check on component mount (handles page refresh)
    const { data: initialDocStatus, isPending: initialStatusPending } = useData(async () => {
        if (!actualContractId) {
            return;
        }
        const statusData = await ContractsApi?.uploadDocumentStatus(actualContractId);
        return statusData;
    }, [actualContractId]);

    // Store the latest document status in a ref to avoid stale closure issues
    const latestDocStatusRef = useRef<any>(null);

    // Phase 2: Post-upload status check (existing behavior)
    const { data: docStatus, isPending: docStatusApi } = useData(async () => {
        // Only poll if we have pending or failed documents (from either initial check or new upload)
        const initialArray = Array.isArray(initialDocStatus) ? initialDocStatus : (initialDocStatus?.documents || []);
        const initialGlobalStatus = (!Array.isArray(initialDocStatus) && initialDocStatus?.status) || null;

        const hasPendingDocs = initialArray?.some(doc => 
            doc.status === "pending" || doc.status === "failed"
        ) || initialGlobalStatus === "pending" || initialGlobalStatus === "failed" || (docUpload && docUpload.status === "pending");
        
        if (!hasPendingDocs || !actualContractId) {
            return;
        }
        
        const statusData = await ContractsApi?.uploadDocumentStatus(actualContractId);
        return statusData;
    }, [pollTrigger, actualContractId]); // Only depend on pollTrigger and contractId

    // Merge both status sources (initial + post-upload)
    const mergedDocStatus = useMemo(() => {
        // Prioritize post-upload status if available, otherwise use initial status
        const current = docStatus || initialDocStatus;
        if (!current) return null;

        if (Array.isArray(current)) return current;
        if (Array.isArray(current.documents)) return current.documents;
        
        // If it's just an object with a status, wrap it in an array if possible or return null
        if (current.status) return [{ status: current.status, id: actualContractId }];

        return null;
    }, [docStatus, initialDocStatus, actualContractId]);

    // Update the ref whenever mergedDocStatus changes
    useEffect(() => {
        if (mergedDocStatus) {
            latestDocStatusRef.current = mergedDocStatus;
        }
    }, [mergedDocStatus]);

    // Extract only pending documents from merged status
    const docStatusesMap = useMemo(() => {
        if (!Array.isArray(mergedDocStatus) || mergedDocStatus.length === 0) {
            return null;
        }
        // Only include pending documents for polling
        const pendingDocs = mergedDocStatus.filter((doc) => 
            doc.status === "pending"
        );
        return pendingDocs.length > 0 ? pendingDocs : null;
    }, [mergedDocStatus]);

    // Store interval ID in a ref so we can clear it from anywhere
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Polling mechanism: runs when there are pending documents
    useEffect(() => {
        // Clear any existing interval first
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        
        if (!docStatusesMap || docStatusesMap.length === 0) {
            return;
        }

        intervalRef.current = setInterval(() => {
            // Double-check before triggering poll
            if (latestDocStatusRef.current) {
                const stillHasPending = latestDocStatusRef.current.some(doc => doc.status === "pending");
                if (!stillHasPending) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return;
                }
            }
            setPollTrigger((prev) => prev + 1); // causes useData to re-run
        }, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [docStatusesMap]);

    // Check if there are any pending documents (exclude failed)
    const hasPendingDocs = useMemo(() => {
        if (!docStatusesMap || docStatusesMap.length === 0) return false;
        return docStatusesMap.some(doc => doc.status === 'pending');
    }, [docStatusesMap]);


    // TODO: join documents with aiDocsInfo by id

    const classes = useStyles();
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        setFile(event.target.files[0]);
    }

    const handleBotCallBack = (payload) => {
        setBotUploadPayload({ ...payload });

    };

    

    const uploadCallback = (handleBotCallBack,id=null) => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        handleUploadCallback(handleBotCallBack, id);
    }

        useEffect(() => {
            if (!botUploadError) return;
            (async () => {
                return await ContractsApi?.uploadAllDocumentsToBot(contractId, {
                    contract_id: contractId,
                });
            })();
    
        }, [botUploadError]);
    

    return (
        <Loadable loading={contractsUploadPending || vendorUploadPending || isUplooadingToBot!=null}>
            <Formik
                enableReinitialize={true}
                initialValues={
                    {
                        documentName: '',
                        description: '',
                        file: {},
                    }
                }
                validate={values => {
                    const errors: { documentName?: string; file?: string;description?:string } = {};

                    if (!values.documentName) {
                        errors.documentName = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (values.documentName.length > 100) {
                        errors.documentName =
                            "Document name cannot exceed 100 characters";
                    }

                    if (values.description && values.description.length > 500) {
                        errors.description =
                            "Description cannot exceed 500 characters";
                    }

                    if (!file) {
                        errors.file = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }
                    else {
                        const allowedTypes = [
                            "application/pdf",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        ];
                        const allowedExtensions = [".pdf", ".doc", ".docx"];
                        const fileName = file.name?.toLowerCase() || "";
                        const fileType = file.type;

                        const hasValidExtension = allowedExtensions.some(ext =>
                            fileName.endsWith(ext)
                        );
                        const hasValidType = allowedTypes.includes(fileType);

                        if (!hasValidExtension || !hasValidType) {
                            errors.file =
                                "Only PDF and Word (.doc, .docx) files are allowed";
                        }
                    }


                    return errors;
                }}
                onSubmit={(values, { resetForm }) => {
                    const metadata = {
                        type: file.type,
                    }
                    const documentObj = {
                        contractId,
                        documentName: values.documentName,
                        metadata: JSON.stringify(metadata),
                        description: values.description,
                    }
                    if (module === MODULE_TYPE_VENDOR) {
                        vendorUpload(file, documentObj, uploadCallback)
                        resetForm();
                    } else {
                        contractUpload(file, documentObj, (id)=> uploadCallback(handleBotCallBack,id));
                        resetForm();
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                }) => (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off">
                        <Card>
                            <Card.Header as="h5">Upload Document</Card.Header>
                            <Card.Body>
                                <Grid container>
                                    <Grid item xs={6} md={3}>
                                        <TextField
                                            required
                                            label="Document Name"
                                            name="documentName"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.documentName}
                                            helperText={touched.documentName && errors.documentName}
                                            error={Boolean(touched.documentName && errors.documentName)}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField
                                            type="text"
                                            multiline
                                            label="Description"
                                            name="description"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            helperText={touched.description && errors.description}
                                            error={Boolean(touched.description && errors.description)}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <TextField
                                            type="file"
                                            label="Upload Contract"
                                            name="file"
                                            inputRef={fileInputRef}
                                            className={classes.textField}
                                            margin="normal"
                                            onChange={handleFileChange}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required
                                            helperText={touched.file && errors.file}
                                            error={Boolean(touched.file && errors.file)}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            // type="submit"
                                            onClick={ () => handleSubmit() }
                                            className={classes.button}
                                            startIcon={<CloudUploadIcon />}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        >
                                            Upload
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DocumentView isEditView={true} contractId={contractId} module={module} shouldDeleteDocument={shouldDeleteDocument}/>
                                    </Grid>
                                </Grid>
                            </Card.Body>
                        </Card>
                    </form>
                )}
            </Formik>
        </Loadable>
    );
}
